/**
 * Paper-Trade-Log: alle Trades aller eigenen Strategien einsehen.
 *
 * Funktionen:
 *   1. Pagination + hohe Limits — Historie komplett durchscrollbar
 *   2. Filter (Strategie, Markt, Datum, Seite)
 *   3. Trade-Details (Modal mit Reason, Indikator-Werten)
 *   4. Stats pro Strategie + Gesamt (Trefferquote, P&L, Profit-Faktor)
 *   5. Toggle "Einzeltrades / Trade-Paare" (FIFO-Matching)
 *   6. CSV-Export
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { apiFetch } from '../../utils/apiFetch';

// --- Typen ---

interface Trade {
  id: number;
  market: string;
  side: 'buy' | 'sell';
  amount: number;
  price: number;
  total: number;
  fee: number;
  status: string;
  strategy_id: number | null;
  realized_pnl: number;
  reason: string | null;
  created_at: string | null;
}

interface TradePair {
  buy_id: number;
  sell_id: number;
  strategy_id: number | null;
  market: string;
  amount: number;
  buy_price: number;
  sell_price: number;
  buy_total: number;
  sell_total: number;
  realized_pnl: number;
  holding_hours: number;
  opened_at: string | null;
  closed_at: string | null;
  buy_reason: string | null;
  sell_reason: string | null;
}

interface TradeStats {
  total_trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  total_pnl: number;
  avg_pnl: number;
  profit_factor: number | null;
  best_trade: number;
  worst_trade: number;
  by_strategy: Record<string, {
    trades: number;
    wins: number;
    losses: number;
    win_rate: number;
    total_pnl: number;
    avg_pnl: number;
    profit_factor: number;
  }>;
}

interface StrategyOption {
  id: number;
  name: string;
}

const fmtEur = (val: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
    .format(val);
const fmtNum = (val: number, decimals = 6) =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(val);
const fmtDate = (ts: string | null) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
};

const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------

const PaperTradeLog: React.FC = () => {
  const [viewMode, setViewMode] = useState<'trades' | 'pairs'>('trades');
  const [trades, setTrades] = useState<Trade[]>([]);
  const [pairs, setPairs] = useState<TradePair[]>([]);
  const [stats, setStats] = useState<TradeStats | null>(null);
  const [strategies, setStrategies] = useState<StrategyOption[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter
  const [strategyId, setStrategyId] = useState<number | ''>('');
  const [market, setMarket] = useState<string>('');
  const [side, setSide] = useState<'buy' | 'sell' | ''>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Modal
  const [selectedTrade, setSelectedTrade] = useState<Trade | TradePair | null>(null);

  const buildQueryString = useCallback((extraPage?: number) => {
    const params = new URLSearchParams();
    if (strategyId !== '') params.set('strategy_id', String(strategyId));
    if (market) params.set('market', market);
    // side gilt nur fuer Einzeltrades — Trade-Pairs ignoriert ihn ohnehin (Audit Bug 6).
    if (side && viewMode === 'trades') params.set('side', side);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    params.set('page', String(extraPage ?? page));
    params.set('page_size', String(PAGE_SIZE));
    return params.toString();
  }, [strategyId, market, side, fromDate, toDate, page, viewMode]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildQueryString();
      const path = viewMode === 'trades' ? '/paper/trades' : '/paper/trades/pairs';
      const res = await apiFetch(`/paper/trades${viewMode === 'pairs' ? '/pairs' : ''}?${qs}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Fehler beim Laden');
      if (viewMode === 'trades') {
        setTrades(Array.isArray(data?.trades) ? data.trades : []);
        setPairs([]);
      } else {
        setPairs(Array.isArray(data?.pairs) ? data.pairs : []);
        setTrades([]);
      }
      setTotal(typeof data?.total === 'number' ? data.total : 0);

      // Stats parallel laden — Filter (ohne Pagination/Side) gelten genauso
      const statsParams = new URLSearchParams();
      if (strategyId !== '') statsParams.set('strategy_id', String(strategyId));
      if (market) statsParams.set('market', market);
      if (fromDate) statsParams.set('from_date', fromDate);
      if (toDate) statsParams.set('to_date', toDate);
      const sRes = await apiFetch(`/paper/trades/stats?${statsParams.toString()}`);
      if (sRes.ok) setStats(await sRes.json());
    } catch (err: any) {
      setError(err?.message || 'Unerwarteter Fehler');
    } finally {
      setLoading(false);
    }
    // intentional dependency list — keep silently
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, page, strategyId, market, side, fromDate, toDate]);

  // Strategien laden (einmalig, fuer Filter-Dropdown).
  // Trailing-Slash ist wichtig — FastAPI redirected sonst per 307,
  // was nicht zuverlaessig durch alle Auth-Schichten kommt.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/strategies/');
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : (Array.isArray(data?.strategies) ? data.strategies : []);
        setStrategies(list.map((s: any) => ({
          id: s.id,
          name: s.name || `Strategie #${s.id}`,
        })));
      } catch {
        // ignore — Filter-Dropdown bleibt leer, Trade-Log laed trotzdem
      }
    })();
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Bei Filter-Wechsel zurueck auf Seite 1
  useEffect(() => {
    setPage(1);
  }, [strategyId, market, side, fromDate, toDate, viewMode]);

  const resetFilters = () => {
    setStrategyId('');
    setMarket('');
    setSide('');
    setFromDate('');
    setToDate('');
  };

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (strategyId !== '') params.set('strategy_id', String(strategyId));
    if (market) params.set('market', market);
    if (side) params.set('side', side);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    try {
      const res = await apiFetch(`/paper/trades/export?${params.toString()}`);
      if (!res.ok) throw new Error('Export fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paper-trades-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Export fehlgeschlagen');
    }
  };

  const strategyName = (id: number | null) => {
    if (id == null) return 'Manuell';
    const s = strategies.find((x) => x.id === id);
    return s?.name || `Strategie #${id}`;
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  // ----------------------------------------------------------------------

  return (
    <Box>
      {/* Stats-Box */}
      {stats && (
        <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Gesamt-G/V</Typography>
              <Typography variant="h6" sx={{
                color: stats.total_pnl >= 0 ? 'success.main' : 'error.main',
                fontWeight: 700,
              }}>
                {fmtEur(stats.total_pnl)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Trefferquote</Typography>
              <Typography variant="h6" fontWeight={700}>
                {fmtNum(stats.win_rate, 1)} %
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.wins} / {stats.wins + stats.losses} Trades
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Profit-Faktor</Typography>
              <Typography variant="h6" fontWeight={700}>
                {stats.profit_factor == null ? '∞' : fmtNum(stats.profit_factor, 2)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Bester / Schlechtester</Typography>
              <Typography variant="body2">
                <span style={{ color: 'green' }}>{fmtEur(stats.best_trade)}</span>
                {' / '}
                <span style={{ color: 'red' }}>{fmtEur(stats.worst_trade)}</span>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ø {fmtEur(stats.avg_pnl)}
              </Typography>
            </Grid>
          </Grid>

          {/* Pro Strategie */}
          {stats.by_strategy && Object.keys(stats.by_strategy).length > 1 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Aufschluesselung pro Strategie
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {Object.entries(stats.by_strategy || {}).map(([sid, st]) => (
                  <Chip
                    key={sid}
                    size="small"
                    label={`${strategyName(Number(sid) || null)}: ${fmtEur(st.total_pnl)} (${fmtNum(st.win_rate, 0)}%, ${st.trades})`}
                    color={st.total_pnl >= 0 ? 'success' : 'error'}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      {/* Filter-Bar */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Strategie</InputLabel>
              <Select
                value={strategyId}
                label="Strategie"
                onChange={(e) => setStrategyId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">Alle</MenuItem>
                {strategies.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size="small" fullWidth label="Markt"
              placeholder="z.B. BTC-EUR"
              value={market}
              onChange={(e) => setMarket(e.target.value.toUpperCase())}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Seite</InputLabel>
              <Select
                value={side}
                label="Seite"
                onChange={(e) => setSide(e.target.value as 'buy' | 'sell' | '')}
                disabled={viewMode === 'pairs'}
              >
                <MenuItem value="">Alle</MenuItem>
                <MenuItem value="buy">Kauf</MenuItem>
                <MenuItem value="sell">Verkauf</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size="small" fullWidth type="date" label="Von"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size="small" fullWidth type="date" label="Bis"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <IconButton onClick={resetFilters} title="Filter zuruecksetzen">
              <RestartAltIcon />
            </IconButton>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center" justifyContent="space-between">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setViewMode(v)}
          >
            <ToggleButton value="trades">Einzelne Trades</ToggleButton>
            <ToggleButton value="pairs">Trade-Paare</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
            >
              Neu laden
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              CSV-Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Tabelle */}
      <TableContainer component={Paper} variant="outlined">
        {viewMode === 'trades' ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Markt</TableCell>
                <TableCell>Seite</TableCell>
                <TableCell align="right">Menge</TableCell>
                <TableCell align="right">Preis</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">G/V</TableCell>
                <TableCell>Strategie</TableCell>
                <TableCell>Grund</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades.map((t) => (
                <TableRow
                  key={t.id}
                  hover
                  sx={{ cursor: 'pointer',
                    backgroundColor: t.side === 'buy'
                      ? 'rgba(46, 125, 50, 0.04)'
                      : 'rgba(211, 47, 47, 0.04)',
                  }}
                  onClick={() => setSelectedTrade(t)}
                >
                  <TableCell>{fmtDate(t.created_at)}</TableCell>
                  <TableCell><Chip size="small" label={t.market} variant="outlined" /></TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t.side === 'buy' ? 'Kauf' : 'Verkauf'}
                      color={t.side === 'buy' ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell align="right">{fmtNum(t.amount, 6)}</TableCell>
                  <TableCell align="right">{fmtEur(t.price)}</TableCell>
                  <TableCell align="right">{fmtEur(t.total)}</TableCell>
                  <TableCell align="right" sx={{
                    color: t.realized_pnl > 0 ? 'success.main'
                         : t.realized_pnl < 0 ? 'error.main'
                         : 'text.secondary',
                    fontWeight: 600,
                  }}>
                    {t.realized_pnl ? fmtEur(t.realized_pnl) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={strategyName(t.strategy_id)} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{
                    maxWidth: 280, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      {t.reason || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {trades.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Keine Trades gefunden — Filter pruefen.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Eroeffnet</TableCell>
                <TableCell>Geschlossen</TableCell>
                <TableCell>Markt</TableCell>
                <TableCell align="right">Menge</TableCell>
                <TableCell align="right">Kauf</TableCell>
                <TableCell align="right">Verkauf</TableCell>
                <TableCell align="right">G/V</TableCell>
                <TableCell align="right">Haltedauer</TableCell>
                <TableCell>Strategie</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pairs.map((p) => (
                <TableRow
                  key={`${p.buy_id}-${p.sell_id}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedTrade(p)}
                >
                  <TableCell>{fmtDate(p.opened_at)}</TableCell>
                  <TableCell>{fmtDate(p.closed_at)}</TableCell>
                  <TableCell><Chip size="small" label={p.market} variant="outlined" /></TableCell>
                  <TableCell align="right">{fmtNum(p.amount, 6)}</TableCell>
                  <TableCell align="right">{fmtEur(p.buy_price)}</TableCell>
                  <TableCell align="right">{fmtEur(p.sell_price)}</TableCell>
                  <TableCell align="right" sx={{
                    color: p.realized_pnl > 0 ? 'success.main' : 'error.main',
                    fontWeight: 600,
                  }}>
                    {fmtEur(p.realized_pnl)}
                  </TableCell>
                  <TableCell align="right">{fmtNum(p.holding_hours, 1)} h</TableCell>
                  <TableCell>
                    <Chip size="small" label={strategyName(p.strategy_id)} variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
              {pairs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Keine geschlossenen Trade-Paare — entweder keine Trades oder
                      noch keine Verkaeufe getaetigt.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
        </Stack>
      )}

      {/* Detail-Modal */}
      <Dialog
        open={selectedTrade !== null}
        onClose={() => setSelectedTrade(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Trade-Details
          <IconButton
            onClick={() => setSelectedTrade(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTrade && 'side' in selectedTrade ? (
            // Einzeltrade
            <Stack spacing={1}>
              <Typography><b>Datum:</b> {fmtDate((selectedTrade as Trade).created_at)}</Typography>
              <Typography><b>Markt:</b> {(selectedTrade as Trade).market}</Typography>
              <Typography>
                <b>Seite:</b> {(selectedTrade as Trade).side === 'buy' ? 'Kauf' : 'Verkauf'}
              </Typography>
              <Typography><b>Menge:</b> {fmtNum((selectedTrade as Trade).amount)}</Typography>
              <Typography><b>Preis:</b> {fmtEur((selectedTrade as Trade).price)}</Typography>
              <Typography><b>Total:</b> {fmtEur((selectedTrade as Trade).total)}</Typography>
              <Typography><b>Gebuehr:</b> {fmtEur((selectedTrade as Trade).fee)}</Typography>
              {(selectedTrade as Trade).realized_pnl !== 0 && (
                <Typography><b>Realisierter G/V:</b> {fmtEur((selectedTrade as Trade).realized_pnl)}</Typography>
              )}
              <Typography>
                <b>Strategie:</b> {strategyName((selectedTrade as Trade).strategy_id)}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Begruendung</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {(selectedTrade as Trade).reason || 'Keine Begruendung gespeichert.'}
                  </Typography>
                </Paper>
              </Box>
            </Stack>
          ) : selectedTrade ? (
            // Trade-Paar
            <Stack spacing={1}>
              <Typography><b>Eroeffnet:</b> {fmtDate((selectedTrade as TradePair).opened_at)}</Typography>
              <Typography><b>Geschlossen:</b> {fmtDate((selectedTrade as TradePair).closed_at)}</Typography>
              <Typography><b>Markt:</b> {(selectedTrade as TradePair).market}</Typography>
              <Typography><b>Menge:</b> {fmtNum((selectedTrade as TradePair).amount)}</Typography>
              <Typography><b>Kauf-Preis:</b> {fmtEur((selectedTrade as TradePair).buy_price)}</Typography>
              <Typography><b>Verkauf-Preis:</b> {fmtEur((selectedTrade as TradePair).sell_price)}</Typography>
              <Typography><b>Realisierter G/V:</b> {fmtEur((selectedTrade as TradePair).realized_pnl)}</Typography>
              <Typography><b>Haltedauer:</b> {fmtNum((selectedTrade as TradePair).holding_hours, 1)} h</Typography>
              <Typography>
                <b>Strategie:</b> {strategyName((selectedTrade as TradePair).strategy_id)}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Begruendungen</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: 'background.default' }}>
                  <Typography variant="caption" color="text.secondary">Kauf:</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                    {(selectedTrade as TradePair).buy_reason || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Verkauf:</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {(selectedTrade as TradePair).sell_reason || '—'}
                  </Typography>
                </Paper>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PaperTradeLog;
